import { PasseriniAPI } from '@/services/apiService';

export interface UserPreferences {
  tramites?: {
    viewMode?: 'table' | 'kanban';
    filters?: {
      activeSearchText?: string;
      sortField?: string;
      sortDirection?: string;
      dateFrom?: string;
      dateTo?: string;
      dateField?: string;
      escrituraFilter?: string;
      registroFilter?: string;
      estadoFilter?: string;
      assignedUserId?: string;
    };
    columnVisibility?: Record<string, boolean>;
    /** Orden elegido por columna del kanban; sólo guarda las que no usan el default */
    kanbanSort?: Record<
      string,
      { sortField?: string; sortDirection?: 'asc' | 'desc' }
    >;
  };
  otrosTramites?: {
    filters?: {
      sortField?: string;
      sortDirection?: string;
      assignedUserId?: string;
    };
    columnVisibility?: Record<string, boolean>;
  };
}

export interface PreferencesResponse {
  success: boolean;
  data: {
    preferences: UserPreferences;
  };
  message: string;
}

const PREFERENCES_ENDPOINTS = {
  GET: '/user/preferences',
  UPDATE: '/user/preferences',
  UPDATE_PARTIAL: '/user/preferences/partial',
  RESET: '/user/preferences',
} as const;

/**
 * Preferences service - handles all user preference API calls
 */
export class PreferencesService {
  /**
   * Get user preferences from server
   */
  static async getPreferences(): Promise<PreferencesResponse> {
    const response = await PasseriniAPI.get<PreferencesResponse>(
      PREFERENCES_ENDPOINTS.GET
    );
    return response.data;
  }

  /**
   * Update full user preferences
   */
  static async updatePreferences(
    preferences: UserPreferences
  ): Promise<PreferencesResponse> {
    const response = await PasseriniAPI.put<PreferencesResponse>(
      PREFERENCES_ENDPOINTS.UPDATE,
      { preferences }
    );
    return response.data;
  }

  /**
   * Update partial preferences using dot notation path
   * @param path - Dot notation path (e.g., 'tramites.filters')
   * @param value - Value to set at path
   */
  static async updatePartialPreferences(
    path: string,
    value: any
  ): Promise<PreferencesResponse> {
    const response = await PasseriniAPI.put<PreferencesResponse>(
      PREFERENCES_ENDPOINTS.UPDATE_PARTIAL,
      { path, value }
    );
    return response.data;
  }

  /**
   * Reset preferences to defaults
   */
  static async resetPreferences(): Promise<PreferencesResponse> {
    const response = await PasseriniAPI.delete<PreferencesResponse>(
      PREFERENCES_ENDPOINTS.RESET
    );
    return response.data;
  }
}

export const {
  getPreferences,
  updatePreferences,
  updatePartialPreferences,
  resetPreferences,
} = PreferencesService;
